import React, { useState } from 'react';
import { IconHelpFilled } from '../icons/help-filled';
import { PositionType } from '../../../utils/calculate-position';
import { FloatingObject } from '../floating-object/floating-object';
import { cn } from '../../../utils/tailwind';

export type TooltipProps = {
    children: React.ReactNode;
    content?: React.ReactNode | string;
    position?: PositionType;
    size?: 'small' | 'medium' | 'large';
    icon?: boolean;
    arrowPosition?: number;
    block?: boolean;
    cursor?: boolean;
    appearance?: 'app' | 'web';
};

export const Tooltip: React.FC<TooltipProps> = ({
    children,
    content,
    position = 'top-left',
    size = 'medium',
    icon = false,
    arrowPosition,
    block = false,
    appearance = 'app',
}) => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const contentClassName = cn(
        'text-center normal-case',
        appearance === 'web'
            ? 'w-max overflow-y-auto break-words text-balance rounded-[1.2rem] bg-[#72645a] p-[1.2rem] text-[1.4rem] font-normal leading-[1.5] text-white shadow-[0_6px_18px_-8px_rgba(79,67,59,0.24)]'
            : 'max-w-[30rem] rounded-[0.8rem] bg-brand-secondary px-[1rem] py-[0.8rem] text-text-inverse shadow-[0_0_0.6rem_0.2rem_rgba(0,0,0,0.3)]',
        appearance === 'app' && size === 'medium' && 'p-[1.6rem] text-small leading-[2rem]',
        appearance === 'app' && size === 'large' && 'p-[2rem] text-large leading-[2.6rem]',
        appearance === 'web' && size === 'small' && 'p-[0.8rem] text-[1.2rem]',
        appearance === 'web' && size === 'large' && 'p-[1.6rem] text-[1.5rem]',
    );

    return content ? (
        <FloatingObject
            trigger={children}
            content={
                <div
                    role={appearance === 'web' ? 'tooltip' : undefined}
                    data-fit-tooltip-text={appearance === 'web' && typeof content === 'string' ? true : undefined}
                    className={contentClassName}
                    style={
                        appearance === 'web'
                            ? {
                                  maxWidth: 'min(30rem, var(--floating-max-width, calc(100vw - 3.2rem)))',
                                  maxHeight: 'var(--floating-max-height, calc(100vh - 3.2rem))',
                              }
                            : undefined
                    }
                >
                    {content}
                </div>
            }
            position={position}
            isVisible={isTooltipVisible}
            setIsVisible={setIsTooltipVisible}
            arrowPosition={arrowPosition}
            block={block}
            icon={
                icon && (
                    <span className="relative inline-flex text-inherit [&_i]:h-[1.4rem] [&_i]:w-[1.4rem]">
                        <IconHelpFilled />
                    </span>
                )
            }
            cursor={icon}
            size={size}
            arrowColor="secondary"
            arrowColorOverride={appearance === 'web' ? '#72645a' : undefined}
            animateEntrance={appearance === 'web'}
            constrainToDialog={appearance === 'web'}
        />
    ) : (
        <>{children}</>
    );
};
